import React, { useMemo, useState, useCallback } from 'react'
import { useStore, useDispatch } from 'react-redux'
import { Button, message } from 'antd'
import {
  trackPicsPageClick,
  trackLabelBindSuccess
} from '../../../components/PhotoFilter/utils/sensorUtils'
import { useAppSelector } from '../../../../app/hooks'
import { selectSearchFilter } from '../../../../slices/photoFilterSlice'
import { selectPreciseSearch } from '../../../../slices/photosByPreciseSlice'
import {
  getSelectionConfig,
  normalizeToSet,
  PhotoSelectionSource
} from '../../utils/selectionHelpers'
import { buildFetchPhotosParamsWithOptions } from '../../lib/photo'
import { buildParamsWithAuthPolicy } from '../../../../actions/photo'
import { batchBindLabels, batchUnbindLabels } from '../../../photoTag/api'
import {
  trackPicspageResult,
  trackPicspageToast,
  trackPicspageDialogClick
} from '../../../photoTag/utils/track'
import TagPicker from '../../../components/TagPicker'
import { useTeamPhotoTags } from '../../../../app/hooks/useTeamPhotoTags'
import { appendLabelsToPhotos } from '../../../../slices/photosByContentSlice'

interface BatchAddLabelButtonProps {
  dataSource?: PhotoSelectionSource
  onRefresh?: (snapshotFilter?: any) => void
}

const BatchAddLabelButton: React.FC<BatchAddLabelButtonProps> = ({
  dataSource = 'content',
  onRefresh
}) => {
  const store = useStore()
  const dispatch = useDispatch()
  const groupId = useAppSelector((state) => state.group.groupId)
  const queryFilter = useAppSelector(selectSearchFilter)
  const preciseSearch = useAppSelector(selectPreciseSearch)

  const selectionConfig = useMemo(() => getSelectionConfig(dataSource), [
    dataSource
  ])
  const photos = useAppSelector(selectionConfig.selectPhotos)
  const rawCheckedPhotos = useAppSelector(selectionConfig.selectCheckedPhotos)
  const rawUncheckedPhotos = useAppSelector(
    selectionConfig.selectUncheckedPhotos
  )
  const checkAll = useAppSelector(selectionConfig.selectCheckAll)
  const checkedPhotos = useMemo(() => normalizeToSet(rawCheckedPhotos), [
    rawCheckedPhotos
  ])
  const unCheckedPhotos = useMemo(() => normalizeToSet(rawUncheckedPhotos), [
    rawUncheckedPhotos
  ])

  const [pickerVisible, setPickerVisible] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  // 团队全部 active 标签 IDs, 用作 batchUnbindLabels 兜底的 labelIDs 集合,
  // 后端 exclusive 没生效时兜底清掉旧标签 (跟单照片路径对称)。
  const { tags: teamAllTagsForFallback } = useTeamPhotoTags()

  const disabled = useMemo(() => {
    return checkedPhotos.size === 0 && !checkAll
  }, [checkedPhotos.size, checkAll])

  const exclude: string[] = useMemo(() => {
    if (checkAll) {
      return photos
        .filter((photo: any) => unCheckedPhotos.has(photo.seq))
        .map((photo: any) => photo.file)
    }
    return []
  }, [checkAll, photos, unCheckedPhotos])

  const include: string[] = useMemo(() => {
    if (!checkAll) {
      return photos
        .filter((photo: any) => checkedPhotos.has(photo.seq))
        .map((photo: any) => photo.file)
    }
    return []
  }, [checkAll, photos, checkedPhotos])

  const handleClick = useCallback(() => {
    if (disabled) {
      message.warning('请先选择照片')
      return
    }
    trackPicsPageClick({ clickItem: '点击批量添加标签' })
    setPickerVisible(true)
  }, [disabled])

  const handleOK = useCallback(
    async (
      selectedLabelIDs: string[],
      _matchAll: boolean,
      checkedNames?: string[]
    ) => {
      if (selectedLabelIDs.length === 0) {
        message.warning('请至少选择一个标签')
        return
      }
      if (!queryFilter || !groupId) return

      setSubmitting(true)
      const t0 = Date.now()
      // try/catch 单层化:之前外层 catch 跟内层 catch 都弹 message.error,
      // build params 抛错 (罕见) 时也走同一条失败埋点, 行为可观察。
      try {
        const isPreciseSearch = dataSource === 'precise'

        const rawParams = buildFetchPhotosParamsWithOptions({
          queryFilter,
          groupID: groupId,
          lastPageCond: '',
          isPreciseSearch,
          excludeKeywords: isPreciseSearch ? preciseSearch?.notWords : undefined
        })
        // 注入权限策略，保持与查询照片接口一致
        const baseParams = buildParamsWithAuthPolicy(
          store.getState(),
          rawParams
        )

        if (checkAll) {
          baseParams.filter.exclude = exclude
        } else {
          baseParams.filter.include = include
        }

        // 兜底:后端 exclusive 没生效时 (deploy 时差 / 灰度未开) 防止变 append。
        // 跟单照片路径 (PhotoCardTagOverlay / Photo.handleChipReplaceCommit) 同思路。
        // labelIDs 传当前团队所有 active label IDs:「1 张 1 标签」语义下每张实际只删 1 个。
        // 后端 exclusive 部署后这步变 no-op (rel 已被服务端清空), 可安全保留。
        // TODO(后端 exclusive 全量灰度后可删): tracker issue #XXX
        const allTeamLabelIDs = teamAllTagsForFallback
          .map((t: any) => String(t.id))
          .filter(Boolean)
        if (allTeamLabelIDs.length > 0) {
          try {
            await batchUnbindLabels({
              groupID: groupId,
              filter: baseParams.filter,
              labelIDs: allTeamLabelIDs,
              isPreciseSearch
            })
          } catch (e) {
            console.warn('[batchUnbind fallback] failed, continue', e)
          }
        }

        const result = await batchBindLabels({
          groupID: groupId,
          filter: baseParams.filter,
          labelIDs: selectedLabelIDs,
          isPreciseSearch,
          exclusive: true
        })
        trackLabelBindSuccess({
          photoNum: result.boundPhotoCount,
          labelName: (checkedNames || []).join('|'),
          fromPlace: '全部照片页批量打标签'
        })
        trackPicspageResult({
          actionName: 'bind_label_batch',
          actionResult: 'success',
          costTime: Date.now() - t0,
          photoNum: result.boundPhotoCount,
          number1: selectedLabelIDs.length,
          list1: checkedNames || [],
          tabName: 'team_label',
          bool1: true,
          fromPage: 'ai_photo_king'
        })
        trackPicspageToast({
          toastName: 'batch_bind_success',
          fromPage: 'ai_photo_king'
        })
        // 乐观更新 redux 里被选中 photos 的 labels — 不等下次 fetch,
        // bind 成功立即把标签 append 到本地 state, UI 立刻刷新 (跟 BatchTagBar / useBatchTagHandlers 对称).
        // 之前只调 onRefresh 触发 fetch, 延迟 500ms+ 加网络往返, 用户体感"没生效".
        try {
          const affectedSeqs: number[] = checkAll
            ? photos
                .filter((p: any) => !unCheckedPhotos.has(p.seq))
                .map((p: any) => p.seq)
            : photos
                .filter((p: any) => checkedPhotos.has(p.seq))
                .map((p: any) => p.seq)
          const newLabels = selectedLabelIDs.map((id, i) => {
            const tag = teamAllTagsForFallback.find(
              (t: any) => String(t.id) === String(id)
            )
            return tag
              ? {
                  id: String(tag.id),
                  name: tag.name,
                  color: tag.color || '#2979FF'
                }
              : {
                  id: String(id),
                  name: (checkedNames || [])[i] || '',
                  color: '#2979FF'
                }
          })
          if (affectedSeqs.length && newLabels.length) {
            dispatch(
              appendLabelsToPhotos({
                affectedSeqs,
                newLabels,
                exclusive: true
              })
            )
          }
        } catch (_) {
          // 乐观更新失败不影响主流程, 大不了等 onRefresh 后台拉数据
        }
        message.success(
          `成功为 ${result.boundPhotoCount} 张照片添加了 ${result.boundLabelCount} 个标签`
        )
        setPickerVisible(false)
        // 不自动 onRefresh:
        //   - 上面 appendLabelsToPhotos 已经把 chip 乐观写到 redux, UI 立即生效
        //   - 后端 ES sync 有延迟 (~秒级), setTimeout 500ms 后 fetch 拿到的还是旧 doc,
        //     会把刚乐观更新的 chip 覆盖掉 → 用户体感"chip 闪烁/消失"
        //   - 用户主动 F5 / 切换筛选时, 时间足够后端 sync 完成, 自然拿到正确数据
        // onRefresh prop 仍然保留作 API, 调用方不依赖, 也不强求传
      } catch (err) {
        const msg = (err as any).message || '批量打标签失败，请稍后重试'
        message.error(msg)
        trackPicspageResult({
          actionName: 'bind_label_batch',
          actionResult: 'fail',
          costTime: Date.now() - t0,
          errorMsg: msg,
          photoNum: 0,
          number1: selectedLabelIDs.length,
          list1: checkedNames || [],
          tabName: 'team_label',
          bool1: true,
          fromPage: 'ai_photo_king'
        })
        trackPicspageToast({
          toastName: 'batch_bind_fail',
          fromPage: 'ai_photo_king'
        })
      } finally {
        setSubmitting(false)
      }
    },
    [
      queryFilter,
      groupId,
      dataSource,
      preciseSearch,
      checkAll,
      exclude,
      include,
      photos,
      checkedPhotos,
      unCheckedPhotos,
      teamAllTagsForFallback,
      dispatch,
      store,
      onRefresh
    ]
  )

  return (
    <>
      <Button type="link" onClick={handleClick} disabled={disabled}>
        批量添加标签
      </Button>
      <TagPicker
        visible={pickerVisible}
        checkedList={[]}
        matchAll={false}
        hideMatchLogic
        title="批量添加照片标签"
        okText={submitting ? '提交中...' : '确定'}
        fromPlace="全部照片批量添加照片页"
        onCancel={() => setPickerVisible(false)}
        onOK={(selectedIds, matchAll, checkedNames) =>
          handleOK(selectedIds, matchAll, checkedNames)
        }
        onSearch={() =>
          trackPicspageDialogClick({
            popTitle: 'batch_select_labels_add',
            clickItem: 'search',
            fromPage: 'ai_photo_king'
          })
        }
      />
    </>
  )
}

export default BatchAddLabelButton
